import { Modal, Typography } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { MODAL_WIDTH_SM } from '@/theme/buildAntdTheme';
import ModalFooter from '@/UI/ModalFooter';

interface Profile {
  id: string;
  company_id: string;
  company_name: string;
  department: string;
}

interface ClientProfileSelectorProps {
  open: boolean;
  profiles: Profile[];
  onSelect: (profileId: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ClientProfileSelector: React.FC<ClientProfileSelectorProps> = ({
  open,
  profiles,
  onSelect,
  onCancel,
  loading = false,
}) => {
  const [selectedProfileId, setSelectedProfileId] = React.useState<
    string | null
  >(null);
  const { t } = useTranslation();

  const handleSelect = (profileId: string) => {
    setSelectedProfileId(profileId);
  };

  const handleConfirm = () => {
    if (selectedProfileId) {
      onSelect(selectedProfileId);
    }
  };

  return (
    <Modal
      title={t('SELECT_PROFILE_TITLE') || 'Select Profile'}
      open={open}
      onCancel={onCancel}
      okText={t('CONTINUE') || 'Continue'}
      cancelText={t('CANCEL') || 'Cancel'}
      okButtonProps={{ disabled: !selectedProfileId, loading }}
      onOk={handleConfirm}
      width={MODAL_WIDTH_SM}
      footer={(_originNode, { OkBtn, CancelBtn }) => (
        <ModalFooter
          actions={
            <>
              <CancelBtn />
              <OkBtn />
            </>
          }
        />
      )}
    >
      <Typography.Text type="secondary">
        {t('SELECT_PROFILE_MESSAGE') ||
          'You have access to multiple client profiles. Please select one to continue.'}
      </Typography.Text>

      {profiles.map((profile) => (
        <div
          key={profile.id}
          role="button"
          tabIndex={0}
          className={
            selectedProfileId === profile.id
              ? 'hp-profile-card hp-profile-card--selected'
              : 'hp-profile-card'
          }
          onClick={() => handleSelect(profile.id)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleSelect(profile.id);
            }
          }}
        >
          <Typography.Title level={5}>{profile.company_name}</Typography.Title>
          {profile.department && (
            <Typography.Text type="secondary">
              {profile.department}
            </Typography.Text>
          )}
        </div>
      ))}
    </Modal>
  );
};

export default ClientProfileSelector;
